/**
 * Simplified Chat Interface for Embedded Widget
 *
 * This is a lightweight version of ChatInterface.tsx designed for iframe embedding.
 * No authentication required (uses public botId), minimal dependencies.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Send, MessageSquare, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

// Slim scrollbar styles - overlay on content
const scrollbarStyles = `
  .kms-chat-scroll {
    overflow-y: overlay;
  }
  .kms-chat-scroll::-webkit-scrollbar {
    width: 8px;
  }
  .kms-chat-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .kms-chat-scroll::-webkit-scrollbar-thumb {
    background: rgba(203, 213, 224, 0.5);
    border-radius: 4px;
  }
  .kms-chat-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(160, 174, 192, 0.8);
  }
`;

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'error';
  content: string;
  timestamp: Date;
  isRateLimit?: boolean; // Flag for rate limit errors
  sources?: Array<{
    document_name: string;
    page_number?: number;
    confidence: number;
    excerpt: string;
  }>;
}

interface EmbeddedChatProps {
  botId: string;
  apiOrigin: string;
  welcomeMessage?: string;
  themeColor?: string;
  maxMessageLength?: number;
  onClose?: () => void;
}

const EmbeddedChat: React.FC<EmbeddedChatProps> = ({
  botId,
  apiOrigin,
  welcomeMessage = 'How can I help you?',
  themeColor = '#3B82F6',
  maxMessageLength = 2000,
  onClose: _onClose,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAskQuestion = async (question: string) => {
    if (!question.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString() + '_user',
      type: 'user',
      content: question,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setCurrentQuestion('');
    setIsLoading(true);

    try {
      // Call public API endpoint (no authentication required for widget, stateless)
      const endpoint = `${apiOrigin}/api/v1/kms/public/processes/${botId}/qa/ask`;

      const requestBody = {
        question,
        max_context_tokens: 4000,
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Handle rate limiting with friendly message
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const resetTime = response.headers.get('X-RateLimit-Reset');

          let friendlyMessage = "⏱️ You're asking questions a bit too quickly! ";

          if (retryAfter) {
            const seconds = parseInt(retryAfter, 10);
            if (seconds < 60) {
              friendlyMessage += `Please wait ${seconds} seconds before trying again.`;
            } else {
              const minutes = Math.ceil(seconds / 60);
              friendlyMessage += `Please wait ${minutes} minute${minutes > 1 ? 's' : ''} before trying again.`;
            }
          } else if (resetTime) {
            const resetDate = new Date(parseInt(resetTime, 10) * 1000);
            const now = new Date();
            const diffSeconds = Math.ceil((resetDate.getTime() - now.getTime()) / 1000);
            if (diffSeconds > 0 && diffSeconds < 60) {
              friendlyMessage += `Please wait ${diffSeconds} seconds before trying again.`;
            } else {
              friendlyMessage += 'Please try again in a moment.';
            }
          } else {
            friendlyMessage += 'Please try again in a moment.';
          }

          throw new Error(friendlyMessage);
        }

        throw new Error(errorData.detail || 'Failed to get response');
      }

      const data = await response.json();
      const answerData = data.data;

      const aiMessage: ChatMessage = {
        id: Date.now().toString() + '_ai',
        type: 'assistant',
        content: answerData.answer,
        sources: answerData.sources || [],
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (err: unknown) {
      const errorContent =
        err instanceof Error ? err.message : 'Sorry, I encountered an error. Please try again.';
      const isRateLimitError = errorContent.includes('⏱️');

      const errorMessage: ChatMessage = {
        id: Date.now().toString() + '_error',
        type: 'error',
        content: errorContent,
        timestamp: new Date(),
        isRateLimit: isRateLimitError,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();
    handleAskQuestion(currentQuestion);
  };

  return (
    <div className="flex flex-col h-full w-full bg-white">
      {/* Inject scrollbar styles */}
      <style>{scrollbarStyles}</style>

      {/* Messages Area - Scrollable */}
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden p-4 pr-2 pb-40 space-y-4 kms-chat-scroll"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#CBD5E0 transparent',
        }}
      >
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-medium mb-2">Start a Conversation</p>
            <p className="text-sm">{welcomeMessage}</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} themeColor={themeColor} />
            ))}
            {isLoading && (
              <div className="flex items-center gap-2 text-gray-500 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Thinking...</span>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Fixed at bottom */}
      <div className="flex-shrink-0 border-t p-4 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
        <form onSubmit={handleSubmitForm} className="flex gap-2">
          <input
            type="text"
            value={currentQuestion}
            onChange={(e) => setCurrentQuestion(e.target.value)}
            placeholder="Type your message..."
            disabled={isLoading}
            maxLength={maxMessageLength}
            className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={isLoading || !currentQuestion.trim()}
            className="px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: themeColor }}
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </form>
        <div className="text-xs text-gray-500 mt-2 text-right">
          {currentQuestion.length}/{maxMessageLength}
        </div>
      </div>
    </div>
  );
};

const MessageBubble: React.FC<{ message: ChatMessage; themeColor: string }> = ({
  message,
  themeColor,
}) => {
  if (message.type === 'user') {
    return (
      <div className="flex justify-end">
        <div
          className="max-w-[80%] rounded-lg px-4 py-2 text-white"
          style={{ backgroundColor: themeColor }}
        >
          <p className="whitespace-pre-wrap text-sm">{message.content}</p>
        </div>
      </div>
    );
  }

  if (message.type === 'error') {
    // Use softer styling for rate limit errors
    const isRateLimit = message.isRateLimit;
    const bgColor = isRateLimit ? 'bg-amber-50' : 'bg-red-50';
    const borderColor = isRateLimit ? 'border-amber-200' : 'border-red-200';
    const textColor = isRateLimit ? 'text-amber-800' : 'text-red-700';

    return (
      <div className="flex justify-start">
        <div className={`max-w-[80%] ${bgColor} border ${borderColor} rounded-lg px-4 py-2`}>
          <p className={`text-sm ${textColor}`}>{message.content}</p>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] bg-gray-100 rounded-lg p-4">
        <div className="prose prose-sm max-w-none">
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </div>

        {/* Sources */}
        {message.sources && message.sources.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-700 mb-2">
              Sources ({message.sources.length})
            </p>
            <div className="space-y-2">
              {message.sources.map((source, index) => (
                <div key={index} className="bg-white rounded border p-2 text-xs">
                  <div className="font-medium text-gray-900">
                    {source.document_name}
                    {source.page_number && (
                      <span className="text-gray-500 ml-1">• Page {source.page_number}</span>
                    )}
                  </div>
                  <div className="text-gray-600 mt-1">{source.excerpt}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbeddedChat;
